import type { ChangeEvent, FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CloseRounded from '@mui/icons-material/CloseRounded';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useUserListAddModal } from 'src/components/userAdmin/hooks/useUserListAddModal';
import { useUserAdmin } from 'src/components/userAdmin/UserAdminContext';

import { styles } from './index.styles';

const UserListAddModal: FC<{
  isOpen: boolean;
  onClose: () => void;
}> = ({ isOpen, onClose }) => {
  const { t } = useTranslation('admin');
  const [isValid, setIsValid] = useState<boolean | undefined>(undefined);
  const [emailValidation, setEmailValidation] = useState<string | undefined>();
  const inputRef = useRef<HTMLInputElement>(null);

  const { searchString, setSearchString } = useUserAdmin();
  const { handleAddUser } = useUserListAddModal({
    isOpen,
    onClose,
    setEmailValidation,
  });

  // clear email validation message if modal is closed
  useEffect(() => {
    if (!isOpen) {
      setEmailValidation(undefined);
    }
  }, [isOpen]);

  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle component="div">
        <Stack direction="row" sx={styles.headerMenu}>
          <IconButton onClick={onClose}>
            <CloseRounded color="primary" />
          </IconButton>
        </Stack>
        <Typography variant="h3" sx={styles.headline}>
          {t('AddUserModal.AddUser')}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <DialogContentText>{t('AddUserModal.EnterMail')}</DialogContentText>
        <Stack
          sx={{
            pb: 1,
          }}
        >
          <TextField
            type="email"
            value={searchString}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              const textInput = event.target.value;
              setSearchString(textInput);
              setIsValid(!!textInput && event.target.checkValidity());
            }}
            inputRef={inputRef}
            placeholder="Email"
            error={!!emailValidation}
            helperText={emailValidation}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="cta" color="secondary" onClick={onClose} fullWidth>
          {t('common:cancel')}
        </Button>
        <Button
          variant="cta"
          color="primary"
          onClick={() => {
            const valid =
              isValid !== undefined
                ? isValid
                : !!searchString && inputRef.current?.validity?.valid;
            if (!valid) {
              setEmailValidation(t('AddUserModal.InvalidMail'));
              return;
            }
            setEmailValidation(undefined);
            handleAddUser(searchString);
          }}
          fullWidth
        >
          {t('List.AddUser')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserListAddModal;
